import { useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  IconSpark,
  IconPackage,
  IconClock,
  IconSearch,
  IconCheck,
  IconWindows,
  IconApple,
  IconChevronR,
} from '../components/Icons';

interface DocSection {
  id: string;
  title: string;
  icon: ReactNode;
  subsections?: DocSubsection[];
}

interface DocSubsection {
  id: string;
  title: string;
  content: ReactNode;
}

export default function Docs() {
  const { t } = useTranslation(['common', 'docs']);
  const { isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedSection, setSelectedSection] = useState(searchParams.get('section') || 'getting-started');
  const [searchQuery, setSearchQuery] = useState('');

  const sections: DocSection[] = [
    {
      id: 'getting-started',
      title: t('docs:sections.gettingStarted.title', 'Getting started'),
      icon: <IconSpark />,
      subsections: [
        {
          id: 'overview',
          title: t('docs:sections.gettingStarted.overview.title', 'Overview'),
          content: <GettingStartedOverview />,
        },
      ],
    },
    {
      id: 'builds',
      title: t('docs:sections.builds.title', 'Builds'),
      icon: <IconPackage />,
      subsections: [
        {
          id: 'webgl-testing',
          title: t('docs:sections.builds.webglTesting.title', 'Testing WebGL builds'),
          content: <WebGLTestingGuide />,
        },
      ],
    },
    {
      id: 'troubleshooting',
      title: t('docs:sections.troubleshooting.title', 'Troubleshooting'),
      icon: <IconClock />,
      subsections: [],
    },
  ];

  const handleSectionChange = (sectionId: string) => {
    setSelectedSection(sectionId);
    setSearchParams({ section: sectionId });
  };

  const filterMatches = (text: string) =>
    !searchQuery.trim() || text.toLowerCase().includes(searchQuery.toLowerCase());

  const getCurrentContent = () => {
    const [sectionId, subsectionId] = selectedSection.split('.');
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return <div>Section not found</div>;
    if (subsectionId && section.subsections) {
      const sub = section.subsections.find((s) => s.id === subsectionId);
      return sub?.content || <div>Subsection not found</div>;
    }
    return section.subsections?.[0]?.content || <div>No content available</div>;
  };

  const docsBody = (
    <>
      <aside className="card" style={{ padding: 18, alignSelf: 'start', position: isMobile ? 'static' : 'sticky', top: 24 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>
          {t('docs:navigation.title', 'Documentation')}
        </div>

        <div style={{ position: 'relative', marginBottom: 14 }}>
          <span
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-6)',
              display: 'inline-flex',
            }}
          >
            <IconSearch size={14} />
          </span>
          <input
            type="text"
            placeholder={t('docs:navigation.search', 'Search docs')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input"
            style={{ paddingLeft: 30 }}
          />
        </div>

        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {sections.filter((s) => filterMatches(s.title)).map((section) => (
            <div key={section.id}>
              <button
                type="button"
                onClick={() => handleSectionChange(section.id)}
                className={'side-link' + (selectedSection.startsWith(section.id) ? ' active' : '')}
                style={{
                  width: '100%',
                  background: selectedSection.startsWith(section.id) ? undefined : 'transparent',
                  border: 'none',
                }}
              >
                <span className="ic">{section.icon}</span>
                <span>{section.title}</span>
              </button>
              {section.subsections && selectedSection.startsWith(section.id) && section.subsections.length > 0 && (
                <div style={{ marginLeft: 28, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {section.subsections.map((sub) => {
                    const id = `${section.id}.${sub.id}`;
                    const isActive = selectedSection === id;
                    return (
                      <button
                        key={sub.id}
                        type="button"
                        onClick={() => handleSectionChange(id)}
                        className="nav-link"
                        style={{
                          justifyContent: 'flex-start',
                          background: 'transparent',
                          border: 'none',
                          color: isActive ? 'var(--accent)' : 'var(--text-5)',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          fontSize: 12,
                        }}
                      >
                        {sub.title}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </nav>
      </aside>

      <article style={{ minWidth: 0 }}>{getCurrentContent()}</article>
    </>
  );

  if (isAuthenticated) {
    return (
      <AppShell
        active="docs"
        defaultSidebarCollapsed
        breadcrumbs={[
          { label: t('common:sidebar.workspace', 'Workspace') },
          { label: t('common:sidebar.docs', 'Docs') },
        ]}
      >
        <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? '24px 16px' : '40px 56px 56px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '260px 1fr', gap: isMobile ? 20 : 32 }}>
          {docsBody}
        </div>
      </AppShell>
    );
  }

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--bg)', color: 'var(--text)', display: 'flex', flexDirection: 'column' }}>
      <Header currentPage="docs" />

      <main style={{ flex: 1, padding: isMobile ? '24px 16px' : '40px 80px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '260px 1fr', gap: isMobile ? 20 : 32, maxWidth: 1280, margin: 0, width: '100%' }}>
        {docsBody}
      </main>

      <Footer />
    </div>
  );
}

function H1({ children }: { children: ReactNode }) {
  return (
    <h1 className="display tight" style={{ fontSize: 36, margin: '0 0 14px' }}>
      {children}
    </h1>
  );
}
function H2({ children }: { children: ReactNode }) {
  return (
    <h2 className="display" style={{ fontSize: 22, margin: '32px 0 12px' }}>
      {children}
    </h2>
  );
}
function P({ children }: { children: ReactNode }) {
  return (
    <p className="muted-2" style={{ fontSize: 14, lineHeight: 1.65, margin: '0 0 14px' }}>
      {children}
    </p>
  );
}
function Code({ children }: { children: ReactNode }) {
  return (
    <div
      className="mono"
      style={{
        background: 'var(--bg-2)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: '12px 14px',
        fontSize: 12.5,
        color: 'var(--text-2)',
        marginBottom: 12,
      }}
    >
      {children}
    </div>
  );
}

function GettingStartedOverview() {
  const { t } = useTranslation(['docs']);
  return (
    <div>
      <div className="eyebrow" style={{ marginBottom: 12 }}>
        {t('docs:sections.gettingStarted.overview.title', 'Overview')}
      </div>
      <H1>{t('docs:sections.gettingStarted.overview.welcome', 'Welcome to Entire.')}</H1>
      <P>{t('docs:sections.gettingStarted.overview.description', 'Entire turns a prompt into a working 2D game. Describe what you want, iterate by chatting, and ship builds for Web, Windows, and Android.')}</P>

      <H2>{t('docs:sections.gettingStarted.overview.quickStart', 'Quick start')}</H2>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {[
          [
            '01',
            t('docs:sections.gettingStarted.overview.step1.title', 'Describe your game'),
            t('docs:sections.gettingStarted.overview.step1.description', 'Write a sentence or two about the game you want — genre, mood, mechanics.'),
          ],
          [
            '02',
            t('docs:sections.gettingStarted.overview.step2.title', 'Generate the first build'),
            t('docs:sections.gettingStarted.overview.step2.description', 'Entire produces code and assets, then queues a playable WebGL build.'),
          ],
        ].map(([n, title, body]) => (
          <div key={n as string} className="card" style={{ padding: 20 }}>
            <div className="mono" style={{ color: 'var(--accent)', fontSize: 11, marginBottom: 10 }}>{n}</div>
            <div className="display" style={{ fontSize: 18, marginBottom: 6 }}>{title}</div>
            <div className="muted-2" style={{ fontSize: 13, lineHeight: 1.5 }}>{body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WebGLTestingGuide() {
  const { t } = useTranslation(['docs']);
  return (
    <div>
      <div className="eyebrow" style={{ marginBottom: 12 }}>
        {t('docs:sections.builds.title', 'Builds')}
      </div>
      <H1>{t('docs:sections.builds.webglTesting.title', 'Testing WebGL builds')}</H1>
      <P>
        {t('docs:sections.builds.webglTesting.description', 'Use the included launcher scripts to spin up a local server and open your build in a browser.')}
      </P>

      <div className="card" style={{ padding: 20, marginBottom: 24 }}>
        <div className="eyebrow" style={{ color: 'var(--accent)', marginBottom: 10 }}>
          {t('docs:sections.builds.webglTesting.quickStart.title', 'Quick start')}
        </div>
        <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[1, 2, 3].map((n) => (
            <li key={n} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span className="mono" style={{ color: 'var(--accent)', minWidth: 18, fontSize: 12 }}>0{n}</span>
              <span className="muted-2" style={{ fontSize: 13.5, lineHeight: 1.55 }}>
                {t(`docs:sections.builds.webglTesting.quickStart.step${n}`)}
              </span>
            </li>
          ))}
        </ol>
      </div>

      <H2>{t('docs:sections.builds.webglTesting.platformInstructions.title', 'Platform-specific')}</H2>

      {[
        { id: 'windows', icon: <IconWindows />, name: 'Windows' },
        { id: 'macos', icon: <IconApple />, name: 'macOS' },
        { id: 'linux', icon: <IconChevronR />, name: 'Linux' },
      ].map((p) => (
        <div key={p.id} className="card" style={{ padding: 20, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <span style={{ color: 'var(--text-2)', display: 'inline-flex' }}>{p.icon}</span>
            <div className="display" style={{ fontSize: 16 }}>{p.name}</div>
          </div>
          <P>{t(`docs:sections.builds.webglTesting.platformInstructions.${p.id}.description`)}</P>
          <Code>{t(`docs:sections.builds.webglTesting.platformInstructions.${p.id}.instruction`)}</Code>
        </div>
      ))}

      <H2>{t('docs:sections.builds.webglTesting.troubleshooting.title', 'Troubleshooting')}</H2>

      {(['blackScreen', 'serverError', 'pythonNotFound'] as const).map((k) => (
        <div key={k} className="card" style={{ padding: 18, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <IconCheck size={14} style={{ color: 'var(--accent)' }} />
            <div className="display" style={{ fontSize: 14 }}>
              {t(`docs:sections.builds.webglTesting.troubleshooting.${k}.title`)}
            </div>
          </div>
          <P>{t(`docs:sections.builds.webglTesting.troubleshooting.${k}.description`)}</P>
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-5)', fontSize: 13, lineHeight: 1.6 }}>
            <li>{t(`docs:sections.builds.webglTesting.troubleshooting.${k}.solution1`)}</li>
            <li>{t(`docs:sections.builds.webglTesting.troubleshooting.${k}.solution2`)}</li>
            {k === 'blackScreen' && <li>{t(`docs:sections.builds.webglTesting.troubleshooting.${k}.solution3`)}</li>}
          </ul>
        </div>
      ))}
    </div>
  );
}
